import { defineStore } from 'pinia'
import { useAppStore } from './app'
import wsClient from '@/services/websocket'

/**
 * Normaliza o status da transação para valores padronizados.
 * Aceita variantes da API ('success', 'confirmada', etc.) e retorna 'confirmed', 'failed' ou 'pending'.
 */
function normalizeStatus(status) {
  if (!status) return 'confirmed'
  const s = status.toLowerCase()
  if (s === 'confirmed' || s === 'success' || s === 'confirmada') return 'confirmed'
  if (s === 'failed' || s === 'falha' || s === 'error') return 'failed'
  if (s === 'pending' || s === 'pendente') return 'pending'
  return 'confirmed'
}

export const useBlockchainStore = defineStore('blockchain', {
  state: () => ({
    // Blocos
    latestBlocks: [],
    blocksCache: {}, // Cache de blocos por número
    totalBlocks: 0,
    
    // Transações
    latestTransactions: [],
    transactionsCache: {}, // Cache de transações por hash
    pendingTransactions: [],
    
    // Validadores
    validators: [],
    validatorsCache: {}, // Cache de validadores por endereço
    
    // WebSocket connection state
    wsConnected: false,
    
    // Estado de sincronização
    isLoadingBlocks: false,
    isLoadingTransactions: false,
    isLoadingValidators: false,
    
    // Estatísticas da blockchain
    stats: {
      transactionsPerBlock: 0,
      averageBlockTime: 0, // em segundos
      totalTransactions: 0,
      totalAccounts: 0,
      totalStake: 0,
      difficulty: 0
    }
  }),
  
  getters: {
    // Getters relacionados a blocos
    getBlockById: (state) => (id) => {
      // Retorna do cache se existir
      if (state.blocksCache[id]) {
        return state.blocksCache[id]
      }
      return null
    },
    
    getLatestBlockNumber: (state) => {
      if (state.latestBlocks.length > 0) {
        return state.latestBlocks[0].number
      }
      return 0
    },
    
    // Getters relacionados a transações
    getTransactionByHash: (state) => (hash) => {
      // Retorna do cache se existir
      if (state.transactionsCache[hash]) {
        return state.transactionsCache[hash]
      }
      return null
    },
    
    getPendingTransactionsCount: (state) => {
      return state.pendingTransactions.length
    },
    
    // Getters relacionados a validadores
    getValidatorByAddress: (state) => (address) => {
      // Retorna do cache se existir
      if (state.validatorsCache[address]) {
        return state.validatorsCache[address]
      }
      return null
    },
    
    getActiveValidatorsCount: (state) => {
      return state.validators.filter(v => v.status === 'active').length
    },
    
    getTotalStake: (state) => {
      return state.validators.reduce((sum, validator) => sum + validator.stake, 0)
    },

    // Getters de agrupamento geográfico
    validatorsByCountry: (state) => {
      const groups = {}
      for (const v of state.validators) {
        const key = v.country || 'unknown'
        if (!groups[key]) groups[key] = []
        groups[key].push(v)
      }
      return groups
    },

    validatorsByRegion: (state) => {
      const groups = {}
      for (const v of state.validators) {
        const key = `${v.country || 'unknown'}:${v.region || 'unknown'}`
        if (!groups[key]) groups[key] = []
        groups[key].push(v)
      }
      return groups
    },
    
    // Getters relacionados a estatísticas
    getAverageBlockTimeFormatted: (state) => {
      const seconds = state.stats.averageBlockTime
      if (seconds < 60) {
        return `${seconds.toFixed(2)} segundos`
      } else {
        const minutes = seconds / 60
        return `${minutes.toFixed(2)} minutos`
      }
    }
  },
  
  actions: {
    // Ações para carregar blocos
    async fetchLatestBlocks(limit = 10) {
      const appStore = useAppStore()
      this.isLoadingBlocks = true
      appStore.setLoading(true)
      
      try {
        // Importar o serviço de API
        const { ExplorerAPI } = await import('@/services/api')
        
        // Buscar blocos da API real
        console.log(`Carregando últimos ${limit} blocos da API...`)
        const blocos = await ExplorerAPI.blocos.obterUltimos(limit)
        
        // Verificar se a resposta da API tem dados válidos
        if (!blocos || blocos.length === 0) {
          console.warn('API retornou uma lista vazia de blocos')
          // Se não temos dados, manter os existentes
          if (this.latestBlocks.length > 0) {
            appStore.addNotification({
              type: 'warning',
              message: 'Sem novos blocos disponíveis no momento',
              timeout: 3000
            })
            return
          }
        }
        
        // Formatar os blocos para o padrão da aplicação
        const formattedBlocks = blocos.map(block => ({
          number: block.slot || block.number || block.altura || 0,
          hash: block.hash || '',
          previousHash: block.previous_hash || '',
          stateRoot: block.state_root || '',
          timestamp: block.timestamp ? new Date(block.timestamp * 1000) : new Date(),
          validator: block.validator || block.validador || '',
          transactionCount: block.tx_count || block.transactionCount || block.transacoes || 0,
          txSuccess: block.tx_success || 0,
          size: block.size || block.tamanho || 0,
          gasUsed: block.gas_used || block.gasUsed || block.gasUtilizado || 0,
          gasLimit: block.gasLimit || block.limiteGas || 10000000
        }))
        
        this.latestBlocks = formattedBlocks
        
        // Atualiza o cache de blocos
        formattedBlocks.forEach(block => {
          this.blocksCache[block.number] = block
        })
        
        // Buscar o total de blocos da blockchain
        try {
          const totalBlocos = await ExplorerAPI.blocos.obterTotal()
          this.totalBlocks = totalBlocos || 0
        } catch (err) {
          console.error('Erro ao obter total de blocos:', err)
        }
        
        // Atualiza estatísticas
        this.updateBlockchainStats()
        
      } catch (error) {
        // Silent on network errors — data will load on next refresh
        if (this.latestBlocks.length === 0) {
          console.debug('[Blockchain] Block load failed:', error.message)
        }
      } finally {
        this.isLoadingBlocks = false
        appStore.setLoading(false)
      }
    },
    
    async fetchBlockById(id) {
      // Se já está no cache, retorna imediatamente
      if (this.blocksCache[id]) {
        return this.blocksCache[id]
      }
      
      const appStore = useAppStore()
      this.isLoadingBlocks = true
      
      try {
        // Importar o serviço de API
        const { ExplorerAPI } = await import('@/services/api')
        
        // Buscar bloco da API real
        console.log(`Carregando detalhes do bloco #${id} da API...`)
        const blocoData = await ExplorerAPI.blocos.obterPorNumero(parseInt(id))
        
        if (!blocoData) {
          console.warn(`Bloco #${id} não encontrado na API`)
          return null
        }
        
        // Formatar o bloco para o padrão da aplicação
        const block = {
          number: blocoData.slot || blocoData.number || blocoData.altura || parseInt(id),
          hash: blocoData.hash || '',
          previousHash: blocoData.previous_hash || blocoData.previousHash || '',
          parentHash: blocoData.previous_hash || blocoData.parentHash || '',
          stateRoot: blocoData.state_root || blocoData.stateRoot || '',
          timestamp: blocoData.timestamp ? new Date(blocoData.timestamp * 1000) : new Date(),
          validator: blocoData.validator || blocoData.validador || '',
          transactionCount: blocoData.tx_count || blocoData.transactionCount || blocoData.transacoes || 0,
          transactions: blocoData.transactions || blocoData.transacoes_lista || [],
          txSuccess: blocoData.tx_success || 0,
          size: blocoData.size || blocoData.tamanho || 0,
          gasUsed: blocoData.gas_used || blocoData.gasUsed || blocoData.gasUtilizado || 0,
          gasLimit: blocoData.gasLimit || blocoData.limiteGas || 10000000,
          posemScore: blocoData.posemScore || blocoData.posem || 0
        }
        
        // Adiciona ao cache
        this.blocksCache[id] = block
        
        return block
        
      } catch (error) {
        console.debug(`[Blockchain] Block #${id} load failed:`, error.message)
        return null
      } finally {
        this.isLoadingBlocks = false
      }
    },
    
    // Ações para carregar transações
    async fetchLatestTransactions(limit = 10) {
      const appStore = useAppStore()
      this.isLoadingTransactions = true
      appStore.setLoading(true)
      
      try {
        // Importar o serviço de API
        const { ExplorerAPI } = await import('@/services/api')
        
        // Buscar transações da API real
        console.log(`Carregando últimas ${limit} transações da API...`)
        const transacoes = await ExplorerAPI.transacoes.obterUltimas(limit)
        
        // Verificar se a resposta da API tem dados válidos
        if (!transacoes || transacoes.length === 0) {
          console.warn('API retornou uma lista vazia de transações')
          // Se não temos dados, manter os existentes
          if (this.latestTransactions.length > 0) {
            appStore.addNotification({
              type: 'warning',
              message: 'Sem novas transações disponíveis no momento',
              timeout: 3000
            })
            return
          }
        }
        
        // Formatar as transações para o padrão da aplicação
        const formattedTransactions = transacoes.map(tx => ({
          hash: tx.hash || tx.id || `0x${(Math.random().toString(16) + '0'.repeat(64)).slice(2, 66)}`,
          blockNumber: tx.blockNumber || tx.blocoPai || tx.blocoNumero || 0,
          timestamp: tx.timestamp ? new Date(tx.timestamp < 10000000000 ? tx.timestamp * 1000 : tx.timestamp) : new Date(),
          from: tx.from || tx.origem || tx.de || `0x${(Math.random().toString(16) + '0'.repeat(40)).slice(2, 42)}`,
          to: tx.to || tx.destino || tx.para || `0x${(Math.random().toString(16) + '0'.repeat(40)).slice(2, 42)}`,
          value: tx.value || tx.valor || 0,
          gas: tx.gas || tx.gasLimit || 21000,
          gasPrice: tx.gasPrice || tx.taxaGas || 5,
          // Mapear gas_used da API, preservando zero como valor válido
          gasUsed: tx.gas_used != null ? tx.gas_used : (tx.gasUsed != null ? tx.gasUsed : null),
          // Normalizar status para 'confirmed'/'failed'/'pending'
          status: normalizeStatus(tx.status || tx.estado),
          currency: window.__QUARNX_COIN__ || 'tKinX', 
          moeda: window.__QUARNX_COIN__ || 'tKinX'
        }))
        
        this.latestTransactions = formattedTransactions
        
        // Atualiza o cache de transações
        formattedTransactions.forEach(tx => {
          this.transactionsCache[tx.hash] = tx
        })
        
        // Atualizar a estatística de total de transações se possível
        try {
          const estatisticas = await ExplorerAPI.estatisticas.obterDashboard()
          if (estatisticas && estatisticas.transacoesTotal) {
            this.stats.totalTransactions = estatisticas.transacoesTotal
          }
        } catch (err) {
          console.error('Erro ao atualizar estatísticas de transações:', err)
        }
        
      } catch (error) {
        console.debug('[Blockchain] Transaction load failed:', error.message)
      } finally {
        this.isLoadingTransactions = false
        appStore.setLoading(false)
      }
    },
    
    async fetchTransactionByHash(hash) {
      // Se já está no cache, retorna imediatamente
      if (this.transactionsCache[hash]) {
        return this.transactionsCache[hash]
      }
      
      const appStore = useAppStore()
      this.isLoadingTransactions = true
      
      try {
        // Importar o serviço de API
        const { ExplorerAPI } = await import('@/services/api')
        
        // Buscar transação da API real
        console.log(`Carregando detalhes da transação ${hash} da API...`)
        const txData = await ExplorerAPI.transacoes.obterPorHash(hash)
        
        if (!txData) {
          console.warn(`Transação ${hash} não encontrada na API`)
          return null
        }
        
        // Formatar a transação para o padrão da aplicação
        const transaction = {
          hash: txData.hash || hash,
          blockNumber: txData.blockNumber || txData.blocoPai || txData.blocoNumero || txData.block_number || 0,
          blockHash: txData.blockHash || txData.block_hash || '',
          timestamp: txData.timestamp ? new Date(txData.timestamp < 10000000000 ? txData.timestamp * 1000 : txData.timestamp) : new Date(),
          from: txData.from || txData.origem || txData.de || '',
          to: txData.to || txData.destino || txData.para || '',
          value: txData.value || txData.valor || 0,
          gas: txData.gas || txData.gasLimit || txData.gas_limit || 21000,
          gasPrice: txData.gasPrice || txData.gas_price || txData.taxaGas || 0,
          // Mapear gas_used da API, preservando zero como valor válido
          gasUsed: txData.gas_used != null ? txData.gas_used : (txData.gasUsed != null ? txData.gasUsed : null),
          nonce: txData.nonce || 0,
          // Normalizar status
          status: normalizeStatus(txData.status || txData.estado),
          data: txData.data || '',
          input: txData.input || '',
          currency: window.__QUARNX_COIN__ || 'tKinX',
          moeda: window.__QUARNX_COIN__ || 'tKinX'
        }
        
        // Adiciona ao cache
        this.transactionsCache[hash] = transaction
        
        return transaction
        
      } catch (error) {
        console.debug(`[Blockchain] Transaction ${hash} load failed:`, error.message)
        return null
      } finally {
        this.isLoadingTransactions = false
      }
    },
    
    // Ações para carregar validadores
    async fetchValidators() {
      const appStore = useAppStore()
      this.isLoadingValidators = true
      appStore.setLoading(true)
      
      try {
        // Importar o serviço de API
        const { ExplorerAPI } = await import('@/services/api')
        
        // Buscar validadores da API real
        console.log('Carregando validadores da API...')
        const validadores = await ExplorerAPI.validadores.obterTodos()
        
        // Formatar os validadores para o padrão da aplicação
        const formattedValidators = validadores.map(v => ({
          address: v.address || v.endereco || '',
          name: v.name || v.nome || 'Validador',
          stake: v.stake || v.amount || 0,
          merit: v.merit || v.merito || 0,
          blocksProposed: v.blocksProposed || v.blocosCriados || 0,
          uptime: v.uptime || 0,
          status: v.status || 'active',
          lastActive: v.lastActive ? new Date(v.lastActive) : new Date(),
          posemScore: v.posemScore || v.posem || 0,
          // Mapear signature_scheme da API, com fallback para "Ed25519" se ausente
          signatureScheme: v.signature_scheme || v.signatureScheme || 'Ed25519',
          // GeoMetadata
          country: v.country || '',
          region: v.region || '',
          city: v.city || ''
        }))
        
        this.validators = formattedValidators
        
        // Atualiza o cache de validadores
        formattedValidators.forEach(validator => {
          this.validatorsCache[validator.address] = validator
        })
        
        // Atualiza estatísticas
        this.updateBlockchainStats()
        
      } catch (error) {
        console.debug('[Blockchain] Validators load failed:', error.message)
      } finally {
        this.isLoadingValidators = false
        appStore.setLoading(false)
      }
    },
    
    // Atualização das estatísticas da blockchain
    async updateBlockchainStats() {
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const stats = await ExplorerAPI.estatisticas.obterDashboard()
        
        this.stats = {
          transactionsPerBlock: 0,
          averageBlockTime: stats.tempoBloco || stats.blockTime || 0.4,
          totalTransactions: stats.transacoesTotal || stats.totalTransactions || 0,
          totalAccounts: stats.totalAccounts || 0,
          totalContracts: stats.totalContracts || 0,
          totalSupply: stats.totalSupply || 0,
          stateRoot: stats.stateRoot || '',
          lastFinalizedSlot: stats.lastFinalizedSlot || 0,
          tps: stats.tps || stats.transacoesPorSegundo || 0,
          totalStake: this.validators.reduce((sum, v) => sum + (v.stake || 0), 0),
          difficulty: 0,
          currency: stats.currency || 'tKinX',
          networkName: stats.networkName || 'QuarnX-Devnet',
          chainId: stats.chainId || 3
        }
      } catch (e) {
        // Fallback: calcular do que temos
        this.stats = {
          transactionsPerBlock: 0,
          averageBlockTime: 0.4,
          totalTransactions: 0,
          totalAccounts: 0,
          totalStake: this.validators.reduce((sum, v) => sum + (v.stake || 0), 0),
          difficulty: 0
        }
      }
      
      const appStore = useAppStore()
      appStore.updateNetworkStatus({
        blockHeight: this.getLatestBlockNumber,
        activeValidators: this.getActiveValidatorsCount,
        totalValidators: this.validators.length,
        currency: this.stats.currency || 'tKinX',
        networkName: this.stats.networkName || 'QuarnX-Devnet',
        chainId: this.stats.chainId || 3
      })
    },
    
    // --- WebSocket integration ---

    /**
     * Initializes WebSocket subscriptions and connects.
     * Subscribes to status changes and incoming messages.
     */
    initializeWebSocket() {
      // Track connection status
      wsClient.onStatusChange((connected) => {
        this.wsConnected = connected
      })

      // Handle incoming real-time messages
      wsClient.onMessage((message) => {
        if (!message || !message.type || !message.data) return

        if (message.type === 'new_block') {
          this._handleNewBlock(message.data)
        } else if (message.type === 'new_transaction') {
          this._handleNewTransaction(message.data)
        }
      })

      wsClient.connect()
    },

    /**
     * Prepends a new block from WebSocket to the blocks list without losing existing data.
     */
    _handleNewBlock(data) {
      const block = {
        number: data.slot || data.number || 0,
        hash: data.hash || '',
        previousHash: data.previous_hash || '',
        stateRoot: data.state_root || '',
        timestamp: data.timestamp ? new Date(data.timestamp * 1000) : new Date(),
        validator: data.validator || '',
        transactionCount: data.tx_count || 0,
        txSuccess: data.tx_success || 0,
        size: data.size || 0,
        gasUsed: data.gas_used || 0,
        gasLimit: data.gasLimit || 10000000
      }

      // Avoid duplicates by hash
      const exists = this.latestBlocks.some(b => b.hash === block.hash)
      if (!exists) {
        this.latestBlocks.unshift(block)
        this.blocksCache[block.number] = block
        this.totalBlocks = Math.max(this.totalBlocks, block.number)
      }
    },

    /**
     * Prepends a new transaction from WebSocket to the transactions list without losing existing data.
     */
    _handleNewTransaction(data) {
      const tx = {
        hash: data.hash || '',
        blockNumber: data.blockNumber || data.block_number || 0,
        timestamp: data.timestamp ? new Date(data.timestamp * 1000) : new Date(),
        from: data.from || '',
        to: data.to || '',
        value: data.value || 0,
        gas: data.gas || 21000,
        gasPrice: data.gasPrice || data.gas_price || 0,
        gasUsed: data.gas_used != null ? data.gas_used : (data.gasUsed != null ? data.gasUsed : null),
        status: normalizeStatus(data.status),
        currency: window.__QUARNX_COIN__ || 'tKinX',
        moeda: window.__QUARNX_COIN__ || 'tKinX'
      }

      // Avoid duplicates by hash
      const exists = this.latestTransactions.some(t => t.hash === tx.hash)
      if (!exists) {
        this.latestTransactions.unshift(tx)
        this.transactionsCache[tx.hash] = tx
      }
    },

    // Inicialização - carrega dados iniciais e conecta WebSocket
    async initializeData() {
      await Promise.all([
        this.fetchLatestBlocks(),
        this.fetchLatestTransactions(),
        this.fetchValidators()
      ])

      // Start WebSocket after initial data load
      this.initializeWebSocket()
    }
  }
})
